import React, { useEffect } from 'react';

import { useGetExchangeRates } from 'apis/handler/exchange-rate';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { REFERRAL_REWARDS_ENABLED } from 'shared/constants';
import { LANGUAGE_DEFAULT_CURRENCY } from 'shared/lib';
import { lazyWithRetry } from 'shared/lib/lazyRetry';
import { CURRENCY_STORE_KEY, useCurrencyStore } from 'store/useCurrencyStore';
import { Footer } from 'widgets/footer';
import { AppHeader } from 'widgets/header';

import HomePage from '../pages/home/HomePage';
import { LangNotFoundRedirect, LanguageRedirect, LanguageWrapper } from './LanguageRedirect';
import LayoutWithHeaderFooter from './LayoutWithHeaderFooter';

const Page404 = lazyWithRetry(() => import('../pages/page404/Page404'));
const Page500 = lazyWithRetry(() => import('../pages/page500/Page500'));

const ListingPage = lazyWithRetry(() => import('../pages/listing/ListingPage'));
const ListingDetailPage = lazyWithRetry(() => import('../pages/listing/ListingDetailPage'));

const GuidePage = lazyWithRetry(() => import('../pages/guide/GuidePage'));
const KoreanHousingInfoPage = lazyWithRetry(() => import('../pages/korean-housing-info/KoreanHousingInfoPage'));

const MembershipPage = lazyWithRetry(() => import('../pages/membership/MembershipPage'));
const ReviewsPage = lazyWithRetry(() => import('../pages/reviews/ReviewsPage'));

const PlanBillingUpgradePage = lazyWithRetry(() => import('../pages/membership/PlanBillingUpgradePage'));
const PlanBillingPage = lazyWithRetry(() => import('../pages/mypage/PlanBillingPage'));

const JourneyPage = lazyWithRetry(() => import('../pages/journey/JourneyPage'));
const JourneyGuidesPage = lazyWithRetry(() => import('../pages/journey/JourneyGuidesPage'));

const WelcomePage = lazyWithRetry(() => import('../pages/welcome/WelcomePage'));

const MyPage = lazyWithRetry(() => import('../pages/mypage/MyPage'));
const MyNotifications = lazyWithRetry(() => import('../pages/mypage/MyNotificationsPage'));
const MyAccount = lazyWithRetry(() => import('../pages/mypage/MyAccount'));
const MyBookings = lazyWithRetry(() => import('../pages/mypage/MyBookings'));
const MyReviews = lazyWithRetry(() => import('../pages/mypage/MyReviewsPage'));
const MyRewards = lazyWithRetry(() => import('../pages/mypage/MyRewardsPage'));

const RequestRoomPlanPage = lazyWithRetry(() => import('../pages/request-room/RequestRoomPlanPage'));
const RequestRoomFormPage = lazyWithRetry(() => import('../pages/request-room/RequestRoomFormPage'));
const RequestRoomFreeFormPage = lazyWithRetry(() => import('../pages/request-room/RequestRoomFreeFormPage'));
const RequestRoomPaymentSuccessPage = lazyWithRetry(
  () => import('../pages/request-room/RequestRoomPaymentSuccessPage'),
);
const RequestRoomPaymentFailurePage = lazyWithRetry(
  () => import('../pages/request-room/RequestRoomPaymentFailurePage'),
);
const RequestRoomDetailPage = lazyWithRetry(() => import('../pages/request-room/RequestRoomDetailPage'));

const RefundSuccessPage = lazyWithRetry(() => import('../pages/request-room/RefundSuccessPage'));

const CardRegistrationSuccessPage = lazyWithRetry(() => import('../pages/mypage/CardRegistrationSuccessPage'));
const CardRegistrationFailurePage = lazyWithRetry(() => import('../pages/mypage/CardRegistrationFailurePage'));

const MyCardManage = lazyWithRetry(() => import('../features/myPage/ui/Card/ManageCards'));

const OrderDetail = lazyWithRetry(() => import('../pages/order/OrderDetail'));

const ReservationDetailPage = lazyWithRetry(() => import('../pages/reservation/ReservationDetailPage'));

const ShortlistPage = lazyWithRetry(() => import('../pages/shortlist/ShortlistPage'));

const OrderCompletePage = lazyWithRetry(() => import('../pages/order/OrderCompletePage'));

const CartsPage = lazyWithRetry(() => import('../pages/carts/CartsPage'));
const CartsSuccessPage = lazyWithRetry(() => import('../pages/carts/CartsSuccessPage'));

const AboutUsPage = lazyWithRetry(() => import('../pages/about-us/AboutUsPage'));
const AllServicesPage = lazyWithRetry(() => import('../pages/all-services/AllServicesPage'));
const CheckmateMallPage = lazyWithRetry(() => import('../pages/checkmate-mall/CheckmateMallPage'));
const JobsPage = lazyWithRetry(() => import('../pages/jobs/JobsPage'));
const UniversityLandingPage = lazyWithRetry(() => import('../pages/university/UniversityLandingPage'));

const commonRoutes = (
  <>
    <Route index element={<HomePage />} />
    <Route path="listing" element={<ListingPage />} />
    <Route path="listing/:id" element={<ListingDetailPage />} />
    <Route path="guide" element={<GuidePage />} />
    <Route path="reviews" element={<ReviewsPage />} />

    <Route path="request-room/form" element={<RequestRoomFormPage />} />
    {/* 가계약 반려·만료 고객의 무료 신청 — Journey 카드에서 반려 주문 ID 를 달고 진입.
        쿼리스트링이 아닌 경로 파라미터인 이유: 401 인터셉터가 redirect 파라미터에 pathname 만 담아
        쿼리로 두면 로그인 후 주문 ID 를 잃는다. */}
    <Route path="request-room/free/:orderId" element={<RequestRoomFreeFormPage />} />
    <Route path="request-room" element={<RequestRoomPlanPage />} />
    <Route path="request-room/detail/:id" element={<RequestRoomDetailPage />} />

    <Route path="about-us" element={<AboutUsPage />} />
    <Route path="all-services" element={<AllServicesPage />} />
    <Route path="korean-housing-info" element={<KoreanHousingInfoPage />} />
    <Route path="membership" element={<MembershipPage />} />
    <Route path="membership/upgrade" element={<PlanBillingUpgradePage />} />
    <Route path="checkmate-mall" element={<CheckmateMallPage />} />
    <Route path="journey" element={<JourneyPage />} />
    <Route path="journey/guides" element={<JourneyGuidesPage />} />
    {/* 메타 광고 웰컴 랜딩 — 색인 제외(NoIndexMeta), seo.ts/sitemap 미등록 */}
    <Route path="welcome" element={<WelcomePage />} />
    <Route path="jobs" element={<JobsPage />} />
    {/* 대학별 SEO 랜딩 (등록 슬러그만 색인 — universitySlugs.ts) */}
    <Route path="university/:slug" element={<UniversityLandingPage />} />

    <Route path="my" element={<MyPage />}>
      <Route path="applicants/history" element={<MyBookings />} />
      <Route path="applicants/reviews" element={<MyReviews />} />

      <Route path="notifications" element={<MyNotifications />} />
      <Route path="account" element={<MyAccount />} />
      {REFERRAL_REWARDS_ENABLED && <Route path="rewards" element={<MyRewards />} />}
      <Route path="card/manage" element={<MyCardManage />} />
      <Route path="card/registration/success" element={<CardRegistrationSuccessPage />} />
      <Route path="card/registration/failure" element={<CardRegistrationFailurePage />} />

      <Route path="subscription/plan-billing" element={<PlanBillingPage />} />
    </Route>
    <Route path="shortlist" element={<ShortlistPage />} />
    <Route path="order/:orderId" element={<OrderDetail />} />
    <Route path="order/complete" element={<OrderCompletePage />} />
    <Route path="reservation/:id" element={<ReservationDetailPage />} />
    <Route path="carts" element={<CartsPage />} />
    <Route path="carts/success" element={<CartsSuccessPage />} />
  </>
);

const AppContent = () => {
  const { i18n } = useTranslation();
  const setSelectedCurrency = useCurrencyStore((s) => s.setSelectedCurrency);

  // 앱 진입 시 1회 호출 → 성공 시 useExchangeRateStore에 동기화
  // (페이지 이동마다 재호출 X, staleTime 24시간)
  useGetExchangeRates();

  useEffect(() => {
    // 명시적으로 통화를 선택한 적이 있으면(localStorage에 저장됨) 그 선택 존중
    if (localStorage.getItem(CURRENCY_STORE_KEY)) return;

    const lang = i18n.language?.split('-')[0];
    const next = lang ? LANGUAGE_DEFAULT_CURRENCY[lang] : undefined;
    if (next) setSelectedCurrency(next);
  }, [i18n.language, setSelectedCurrency]);

  return (
    <Routes>
      <Route
        path="/404"
        element={
          <>
            <AppHeader />
            <Page404 />
            <Footer />
          </>
        }
      />

      <Route
        path="/:lang/404"
        element={
          <>
            <AppHeader />
            <Page404 />
            <Footer />
          </>
        }
      />

      <Route
        path="/500"
        element={
          <>
            <AppHeader />
            <Page500 />
            <Footer />
          </>
        }
      />

      <Route
        path="/:lang/500"
        element={
          <>
            <AppHeader />
            <Page500 />
            <Footer />
          </>
        }
      />

      <Route
        path="/request-room/payment-success"
        element={
          <>
            <AppHeader />
            <RequestRoomPaymentSuccessPage />
            <Footer />
          </>
        }
      />

      <Route
        path="/:lang/request-room/payment-success"
        element={
          <>
            <AppHeader />
            <RequestRoomPaymentSuccessPage />
            <Footer />
          </>
        }
      />

      <Route
        path="/request-room/payment-failure"
        element={
          <>
            <AppHeader />
            <RequestRoomPaymentFailurePage />
            <Footer />
          </>
        }
      />

      <Route
        path="/:lang/request-room/payment-failure"
        element={
          <>
            <AppHeader />
            <RequestRoomPaymentFailurePage />
            <Footer />
          </>
        }
      />

      <Route
        path="/request-room/refund-success"
        element={
          <>
            <AppHeader />
            <RefundSuccessPage />
            <Footer />
          </>
        }
      />

      <Route
        path="/:lang/request-room/refund-success"
        element={
          <>
            <AppHeader />
            <RefundSuccessPage />
            <Footer />
          </>
        }
      />

      <Route element={<LayoutWithHeaderFooter />}>
        {/* 언어 감지 및 리다이렉트 처리 */}
        <Route element={<LanguageRedirect />}>
          <Route path="/">
            {commonRoutes}
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>

          <Route path="/:lang" element={<LanguageWrapper />}>
            {commonRoutes}
            <Route path="404" element={<Page404 />} />
            <Route path="*" element={<LangNotFoundRedirect />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default AppContent;
